import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { SettingsSample } from '../sampleData';
import type { DetailsValues } from '../state';

import { DetailsCard } from './DetailsCard';
import type { CardEditProps } from './DetailsCard';

/**
 * Where it belongs (brief §3): legal entity, department, project type and
 * category, with no heading of its own (Mette, 3 Nov). Resource planning moved to When (decision 18).
 */
export type BelongsCardProps = {
  values: Pick<DetailsValues, 'legalEntityId' | 'departmentId' | 'projectType' | 'projectCategory'>;
  sample: SettingsSample;
  /** Edit mode, for a card that has its form. */
  edit?: CardEditProps;
};

const Fact = ({
  label,
  value,
  automationId,
}: {
  label: string;
  value: string | null;
  automationId: string;
}) => {
  const { t } = useTranslation('pmDetails');
  return (
    <Stack sx={{ gap: 0.25, minWidth: 0 }} data-automation-id={automationId}>
      <Typography variant="label" component="span" sx={{ color: 'var(--color-fog-80)' }}>
        {label}
      </Typography>
      <Typography
        variant="bodyRegular"
        component="span"
        sx={{ color: value === null ? 'var(--color-fog-80)' : undefined }}
      >
        {value ?? t('belongs.NotSet')}
      </Typography>
    </Stack>
  );
};

export const BelongsCard = ({ values, sample, edit }: BelongsCardProps) => {
  const { t } = useTranslation('pmDetails');
  const legalEntity = sample.legalEntities.find((entity) => entity.id === values.legalEntityId);
  const department = sample.departments.find((entry) => entry.id === values.departmentId);
  return (
    <DetailsCard
      icon="account_tree"
      title={t('belongs.Title')}
      automationId="details-card-belongs"
      {...edit}
    >
      <Stack sx={{ gap: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <Fact
            label={t('belongs.LegalEntity')}
            value={legalEntity?.name ?? null}
            automationId="details-belongs-legal-entity"
          />
          <Fact
            label={t('belongs.Department')}
            value={department?.name ?? null}
            automationId="details-belongs-department"
          />
          <Fact
            label={t('belongs.ProjectType')}
            value={values.projectType}
            automationId="details-belongs-project-type"
          />
          <Fact
            label={t('belongs.Category')}
            value={values.projectCategory}
            automationId="details-belongs-category"
          />
        </Box>
      </Stack>
    </DetailsCard>
  );
};
